// src/components/portfolio/GalleryCarousel.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./GalleryCarousel.module.css";

export default function GalleryCarousel({ items = [], ariaLabel = "Galeria" }) {
  const trackRef = useRef(null);
  const slideRefs = useRef([]);
  const rafRef = useRef(0);

  const safeItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const clampIndex = (n) => {
    if (!safeItems.length) return 0;
    return Math.max(0, Math.min(n, safeItems.length - 1));
  };

  const isMobile = () => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(max-width: 720px)").matches;
  };

  const clampScrollLeft = (el, left) => {
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    return Math.max(0, Math.min(left, max));
  };

  const getClosestIndex = () => {
    const el = trackRef.current;
    if (!el) return 0;

    const slides = slideRefs.current.filter(Boolean);
    if (!slides.length) return 0;

    const mobile = isMobile();

    // Desktop: snap no centro → âncora no centro do viewport
    // Mobile: snap no início (start) → âncora no scrollLeft
    const anchor = mobile ? el.scrollLeft + 1 : el.scrollLeft + el.clientWidth / 2;

    let bestIdx = 0;
    let bestDist = Infinity;

    for (let i = 0; i < slides.length; i += 1) {
      const s = slides[i];

      const point = mobile
        ? s.offsetLeft
        : s.offsetLeft + s.clientWidth / 2;

      const dist = Math.abs(point - anchor);

      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }

    return clampIndex(bestIdx);
  };

  const syncNav = (idx) => {
    const clamped = clampIndex(idx);
    setActiveIndex(clamped);
    setCanPrev(clamped > 0);
    setCanNext(clamped < safeItems.length - 1);
  };

  const scrollToIndex = (idx, behavior = "smooth") => {
    const el = trackRef.current;
    const target = slideRefs.current[idx];
    if (!el || !target) return;

    const mobile = isMobile();

    // Mobile: alinha no início (combina com scroll-snap-align: start do CSS)
    // Desktop: centraliza (combina com scroll-snap-align: center do CSS)
    const left = mobile
      ? target.offsetLeft
      : target.offsetLeft - (el.clientWidth - target.clientWidth) / 2;

    el.scrollTo({ left: clampScrollLeft(el, left), behavior });

    syncNav(idx);
  };

  const go = (dir) => {
    const next = clampIndex(activeIndex + dir);
    if (next === activeIndex) return; // trava começo/fim (não desenquadra)
    scrollToIndex(next, "smooth");
  };

  // Reset quando lista muda
  useEffect(() => {
    slideRefs.current = [];
    syncNav(0);

    const id = requestAnimationFrame(() => {
      if (safeItems.length) scrollToIndex(0, "auto");
    });

    return () => cancelAnimationFrame(id);
  }, [safeItems.length]);

  // Sincroniza activeIndex quando o usuário arrasta (mobile/desktop)
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const idx = getClosestIndex();
        syncNav(idx);
      });
    };

    const onResize = () => {
      // mantém encaixado no slide mais próximo ao rotacionar / resize
      scrollToIndex(getClosestIndex(), "auto");
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [safeItems.length]);

  if (!safeItems.length) return null;

  return (
    <div className={styles.wrap} aria-label={ariaLabel}>
      {/* PC permanece com setas (como no ZIP). No mobile você pode ocultar via CSS se quiser. */}
      <button
        type="button"
        className={styles.nav}
        onClick={() => go(-1)}
        aria-label="Anterior"
        disabled={!canPrev}
      >
        ‹
      </button>

      <div className={styles.track} ref={trackRef}>
        {safeItems.map((it, idx) => (
          <figure
            className={styles.slide}
            key={`${it.src || "img"}-${idx}`}
            ref={(node) => {
              slideRefs.current[idx] = node;
            }}
          >
            <img
              src={it.src}
              alt={it.alt || ""}
              loading="lazy"
              decoding="async"
              className={styles.img}
            />
            {it.caption ? (
              <figcaption className={styles.caption}>{it.caption}</figcaption>
            ) : null}
          </figure>
        ))}
      </div>

      <button
        type="button"
        className={styles.nav}
        onClick={() => go(1)}
        aria-label="Próximo"
        disabled={!canNext}
      >
        ›
      </button>

      {/* Bolinhas: SOMENTE no mobile (não aparece no PC) */}
      {isMobile() ? (
        <div className={styles.dots} aria-label="Paginação da galeria">
          {safeItems.map((_, idx) => {
            const isActive = idx === activeIndex;

            return (
              <button
                key={`dot-${idx}`}
                type="button"
                className={`${styles.dot} ${isActive ? styles.dotActive : ""}`}
                aria-label={`Ir para a imagem ${idx + 1}`}
                aria-current={isActive ? "true" : "false"}
                onClick={() => scrollToIndex(idx, "smooth")}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
